import { Link } from "react-router-dom";
import { 
  Shield, 
  Lock, 
  Sun, 
  User, 
  Package, 
  Heart, 
  Bell, 
  Database, 
  Settings, 
  MapPin, 
  CreditCard, 
  Eye,
  Zap,
  Layers,
  Palette,
  Brain,
  Smartphone,
  ShoppingCart,
  MessageSquare,
  Accessibility,
  Workflow
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";

const accountNav = [
  { 
    name: 'Profile', 
    tab: 'profile', 
    icon: User, 
    description: 'Personal information & avatar',
    badge: null,
    color: 'text-blue-500'
  },
  { 
    name: 'Addresses', 
    tab: 'addresses', 
    icon: MapPin, 
    description: 'Shipping & billing addresses',
    badge: null,
    color: 'text-green-500'
  },
  { 
    name: 'Orders', 
    tab: 'orders', 
    icon: Package, 
    description: 'Order history & tracking',
    badge: '12',
    color: 'text-purple-500'
  },
  { 
    name: 'Wishlist', 
    tab: 'wishlist', 
    icon: Heart, 
    description: 'Saved items & favorites',
    badge: '5',
    color: 'text-pink-500'
  },
  { 
    name: 'Payments', 
    tab: 'payments', 
    icon: CreditCard, 
    description: 'Cards & payment methods',
    badge: null,
    color: 'text-amber-500'
  },
];

const securityNav = [
  { 
    name: 'Security', 
    tab: 'security', 
    icon: Shield, 
    description: 'Password & authentication',
    badge: null,
    color: 'text-emerald-500'
  },
  { 
    name: 'Devices', 
    tab: 'devices', 
    icon: Smartphone, 
    description: 'Active sessions & devices',
    badge: '3',
    color: 'text-blue-500'
  },
  { 
    name: 'Privacy', 
    tab: 'privacy', 
    icon: Eye, 
    description: 'Data privacy & visibility',
    badge: null,
    color: 'text-indigo-500'
  },
];

const preferencesNav = [
  { 
    name: 'Appearance', 
    tab: 'appearance', 
    icon: Palette, 
    description: 'Theme, colors & layout',
    badge: 'New',
    color: 'text-violet-500'
  },
  { 
    name: 'AI Personalization', 
    tab: 'ai-personalization', 
    icon: Brain, 
    description: 'Smart recommendations',
    badge: 'AI',
    color: 'text-cyan-500'
  },
  { 
    name: 'Notifications', 
    tab: 'notifications', 
    icon: Bell, 
    description: 'Email, SMS & push alerts',
    badge: null,
    color: 'text-orange-500'
  },
  { 
    name: 'Communication', 
    tab: 'communication', 
    icon: MessageSquare, 
    description: 'Contact preferences',
    badge: null,
    color: 'text-blue-500'
  },
  { 
    name: 'Shopping', 
    tab: 'shopping', 
    icon: ShoppingCart, 
    description: 'Commerce preferences',
    badge: null,
    color: 'text-emerald-500'
  },
  { 
    name: 'Accessibility', 
    tab: 'accessibility', 
    icon: Accessibility, 
    description: 'Inclusive design options',
    badge: null,
    color: 'text-purple-500'
  },
];

const dataNav = [
  { 
    name: 'Automation', 
    tab: 'automation', 
    icon: Workflow, 
    description: 'Smart rules & workflows',
    badge: 'Beta',
    color: 'text-amber-500'
  },
  { 
    name: 'Data', 
    tab: 'data', 
    icon: Database, 
    description: 'Export & account deletion',
    badge: null,
    color: 'text-red-500'
  },
];

type NavItem = {
  name: string;
  tab: string;
  icon: React.ElementType;
  description: string;
  badge?: string | null;
  color: string;
}

interface ModernSettingsSidebarProps {
  activeTab: string;
}

const ModernSettingsSidebar = ({ activeTab }: ModernSettingsSidebarProps) => {
  const renderNav = (items: NavItem[], category: string) => {
    return (
      <ul className="space-y-2">
        {items.map((item, index) => (
          <motion.li 
            key={item.name}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <Link
              to={`/settings?tab=${item.tab}`}
              className={cn(
                "group relative flex items-center px-4 py-4 rounded-xl transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5",
                activeTab === item.tab
                  ? "bg-gradient-to-r from-primary to-primary/80 text-white shadow-primary/25 shadow-lg"
                  : "text-muted-foreground hover:bg-card/80 hover:text-foreground hover:shadow-md backdrop-blur-sm border border-transparent hover:border-border/50"
              )}
            >
              {/* Active indicator */}
              {activeTab === item.tab && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute inset-0 bg-gradient-to-r from-primary to-primary/80 rounded-xl"
                  transition={{ type: "spring", duration: 0.5 }}
                />
              )}
              
              <div className="relative flex items-center w-full">
                {/* Icon with enhanced styling */}
                <div className={cn(
                  "flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center mr-3 transition-all duration-300",
                  activeTab === item.tab 
                    ? "bg-white/20 backdrop-blur-sm" 
                    : "bg-muted/50 group-hover:bg-primary/10"
                )}>
                  <item.icon className={cn(
                    "h-5 w-5 transition-all duration-300",
                    activeTab === item.tab 
                      ? "text-white" 
                      : `${item.color} group-hover:text-primary`
                  )} />
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className={cn(
                      "font-medium text-sm transition-colors",
                      activeTab === item.tab ? "text-white" : "text-foreground"
                    )}>
                      {item.name}
                    </span>
                    
                    {/* Badge */}
                    {item.badge && (
                      <Badge 
                        variant={activeTab === item.tab ? "secondary" : "outline"}
                        className={cn(
                          "text-xs px-2 py-0.5 transition-all duration-300",
                          activeTab === item.tab 
                            ? "bg-white/20 text-white border-white/30" 
                            : "bg-primary/10 text-primary border-primary/20"
                        )}
                      >
                        {item.badge}
                      </Badge>
                    )}
                  </div>
                  
                  <p className={cn(
                    "text-xs transition-colors line-clamp-1",
                    activeTab === item.tab 
                      ? "text-white/80" 
                      : "text-muted-foreground group-hover:text-muted-foreground/80"
                  )}>
                    {item.description}
                  </p>
                </div>
              </div>
              
              {/* Hover glow effect */}
              <div className={cn(
                "absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none",
                activeTab !== item.tab && "bg-gradient-to-r from-primary/5 to-secondary/5"
              )} />
            </Link>
          </motion.li>
        ))}
      </ul>
    )
  }

  return (
    <Card className="bg-card/60 backdrop-blur-xl border-border/50 shadow-2xl overflow-hidden">
      <div className="bg-gradient-to-b from-primary/5 to-transparent p-1">
        <nav className="p-6 space-y-8">
          {/* Header */}
          <motion.div 
            className="flex items-center gap-3 mb-6"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg">
              <Settings className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Settings</h2>
              <p className="text-xs text-muted-foreground">Manage your account</p>
            </div>
          </motion.div>
          
          {/* Account Management */}
          <div>
            <div className="flex items-center gap-2 px-3 mb-4">
              <Layers className="h-4 w-4 text-primary" />
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Account
              </h3>
              <div className="flex-1 h-px bg-gradient-to-r from-border to-transparent" />
            </div>
            {renderNav(accountNav, 'account')}
          </div>
          
          <Separator className="opacity-30" />
          
          {/* Security */}
          <div>
            <div className="flex items-center gap-2 px-3 mb-4">
              <Shield className="h-4 w-4 text-emerald-500" />
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Security
              </h3>
              <div className="flex-1 h-px bg-gradient-to-r from-border to-transparent" />
            </div>
            {renderNav(securityNav, 'security')}
          </div>
          
          <Separator className="opacity-30" />
          
          {/* Preferences */}
          <div>
            <div className="flex items-center gap-2 px-3 mb-4">
              <Zap className="h-4 w-4 text-orange-500" />
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Preferences
              </h3>
              <div className="flex-1 h-px bg-gradient-to-r from-border to-transparent" />
            </div>
            {renderNav(preferencesNav, 'preferences')}
          </div>
          
          <Separator className="opacity-30" />
          
          {/* Data */}
          <div>
            <div className="flex items-center gap-2 px-3 mb-4">
              <Database className="h-4 w-4 text-red-500" />
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Data & Privacy
              </h3>
              <div className="flex-1 h-px bg-gradient-to-r from-border to-transparent" />
            </div>
            {renderNav(dataNav, 'data')}
          </div>
        </nav>
      </div>
    </Card>
  );
};

export default ModernSettingsSidebar;